// 宿泊代行リクエストの API (#1665)。配置ルールは services/api/index.ts のヘッダ参照。
//
// **許可待ち機能**: backend は LODGING_ENABLED で router ごと 404。画面側の
// gate (isLicensePendingDemoEnabled) と二重に閉じる。
import { apiFetch } from '../../services/api/_client'

/** 当方が代行できる宿。OTA に在庫を出さない直販宿だけが対象 (#1665) */
export type LodgingBrand = 'toyoko_inn'

export type StayRequestStatus =
  | 'requested'     // 申込済み (まだ何も予約されていない)
  | 'in_progress'   // 手配中
  | 'confirmed'     // 確定 (予約番号あり)
  | 'unavailable'   // 満室等で取れなかった
  | 'cancelled'     // 客が取り下げ

/** 客に見える範囲。連絡先・スタッフメモは含まない (サーバの public_view と対) */
export interface StayRequest {
  id: string
  status: StayRequestStatus
  hotelBrand: LodgingBrand
  hotelName: string | null
  hotelNameEn: string | null
  areaId: string | null
  /** しおりのどの滞在地の枠か (予約番号を該当箇所に出すため) */
  tripId: string | null
  stopId: string | null
  checkIn: string
  checkOut: string
  nights: number
  rooms: number
  guests: number
  guestName: string
  roomTypeId: string | null
  roomTypeName: string | null
  roomTypeNameEn: string | null
  roomSqm: number | null
  pricePerNightYen: number | null
  totalYen: number | null
  smoking: 'non_smoking' | 'smoking' | 'any'
  notes: string | null
  confirmationNumber: string | null
  /** デモ環境の自動確定。true の画面では「テスト予約」と明示する */
  isMock: boolean
  messageToGuest: string | null
  createdAt: string
  updatedAt: string
}

/** 空室検索の 1 室分 (実際の東横インと同じく、広さと料金を見てから選ばせる) */
export interface RoomOffer {
  roomTypeId: string
  nameJa: string
  nameEn: string
  sqm: number
  capacity: number
  pricePerNightYen: number
  totalYen: number
  remaining: number
}

export interface AvailabilityResult {
  hotelBrand: LodgingBrand
  hotelName: string
  hotelNameEn: string
  nights: number
  soldOut: boolean
  rooms: RoomOffer[]
  checkInTime: string
  checkOutTime: string
  breakfast: { included: boolean; type: string; from: string; to: string }
}

export const lodgingApi = {
  /** 空室・料金の検索。**予約の前に必ず通す** (空きも金額も分からないまま
   * 申込を飛ばさない)。満室は soldOut: true で返る */
  searchLodgingAvailability: async (params: {
    checkIn: string
    checkOut: string
    guests: number
    rooms: number
    areaId?: string | null
    hotelBrand?: LodgingBrand
  }): Promise<AvailabilityResult> => {
    const q = new URLSearchParams({
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      guests: String(params.guests),
      rooms: String(params.rooms),
      ...(params.areaId ? { areaId: params.areaId } : {}),
      hotelBrand: params.hotelBrand ?? 'toyoko_inn',
    })
    const res = await apiFetch(`/lodging/availability?${q.toString()}`)
    return res.json()
  },

  /** 代行の申込。デモ環境 (LODGING_AUTO_CONFIRM) では即 confirmed で返る */
  createStayRequest: async (payload: {
    hotelBrand: LodgingBrand
    areaId?: string | null
    hotelName?: string | null
    checkIn: string
    checkOut: string
    rooms: number
    guests: number
    guestName: string
    /** 空室検索で選んだ部屋。料金はサーバが引き直す */
    roomTypeId: string
    email?: string
    phone?: string
    smoking: 'non_smoking' | 'smoking' | 'any'
    notes?: string
    locale: string
    tripId?: string | null
    stopId?: string | null
  }): Promise<StayRequest> => {
    const res = await apiFetch('/lodging/stay-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    return res.json()
  },

  getMyStayRequests: async (): Promise<{ requests: StayRequest[] }> => {
    const res = await apiFetch('/lodging/stay-requests/me')
    return res.json()
  },

  cancelStayRequest: async (requestId: string): Promise<StayRequest> => {
    const res = await apiFetch(`/lodging/stay-requests/${requestId}/cancel`, { method: 'POST' })
    return res.json()
  },
}
